import { Middleware, MiddlewareAPI } from "@reduxjs/toolkit";
import { Realtime_item, setStatus } from "./slice";
import { io, Socket } from "socket.io-client";
import * as comment from "../collection/comment/slice";
import * as advertising from "../collection/advertising/slice";
import * as demand from "../collection/demand/slice";
import * as program from "../collection/program/slice";
import * as privilege from "../collection/privilege/slice";
import * as admin from "../collection/user/admin/slice";
import * as client from "../collection/user/client/slice";
import * as operator from "../collection/user/operator/slice";
import * as message from "../collection/message/slice";
import { rootDispatch, rootState } from "../../store";
import * as admin_connected from "../collection/user_connected/admin/slice";
import * as client_connected from "../collection/user_connected/client/slice";
import * as operator_connected from "../collection/user_connected/operator/slice";
import * as guest_connected from "../collection/user_connected/guest/slice";
import * as live from "../live/slice";
import * as actuality from "../collection/actuality/slice";
import * as program_record from "../collection/progam_record/slice";
import * as reporting from "../collection/reporting/slice";
import * as advertising_plan from "../collection/advertising_plan/slice";
import * as payement from "../collection/payement/slice";
import * as flux from "../collection/flux/slice";
import * as subscription from "../collection/subscription/slice";
import * as playlist from "../collection/playlist/slice"

interface SocketMetadata {
  socket: Socket;
  pingInterval?: NodeJS.Timeout;
  healthCheckInterval?: NodeJS.Timeout;
  lastPongReceived: number;
  listenersRegistered: boolean;
}

const clients: Map<Realtime_item, SocketMetadata> = new Map();

const collectionActions: Record<string, any> = {
  advertising: { create: advertising.append, update: advertising.merge, delete: advertising.pop },
  client: { create: client.append, update: client.merge, delete: client.pop },
  demand: { create: demand.append, update: demand.merge, delete: demand.pop },
  comment: { create: comment.append, update: comment.merge, delete: comment.pop },
  message: { create: message.append, update: message.merge, delete: message.pop },
  actuality: { create: actuality.append, update: actuality.merge, delete: actuality.pop },
  programrecord: { create: program_record.append, update: program_record.merge, delete: program_record.pop },
  payement: { create: payement.append, update: payement.merge },
  reporting: { create: reporting.append, update: reporting.merge },
  advertisingplan: { create: advertising_plan.append, update: advertising_plan.merge },
  admin: { create: admin.append, update: admin.merge, delete: admin.pop },
  flux: { create: flux.append, update: flux.update, delete: flux.pop },
  operator: { create: operator.append, update: operator.merge, delete: operator.pop },
  privilege: { create: privilege.append, update: privilege.merge, delete: privilege.pop },
  program: { create: program.append, update: program.merge, delete: program.pop },
  streamrecord: { create: program_record.append, delete: program_record.pop, update: program_record.merge },
  subscription: { create: subscription.append, delete: subscription.pop, update: subscription.merge },
  playlist: { create: playlist.append, delete: playlist.pop, update: playlist.merge }
};

const cleanupSocket = (key: Realtime_item) => {
  const metadata = clients.get(key);
  if (metadata) {
    if (metadata.pingInterval) clearInterval(metadata.pingInterval);
    if (metadata.healthCheckInterval) clearInterval(metadata.healthCheckInterval);
    metadata.socket.removeAllListeners();
    metadata.socket.disconnect();
    clients.delete(key);
  }
};

const setupPingPong = (metadata: SocketMetadata, key: Realtime_item) => {
  const socket = metadata.socket;
  if (metadata.pingInterval) clearInterval(metadata.pingInterval);
  if (metadata.healthCheckInterval) clearInterval(metadata.healthCheckInterval);

  metadata.lastPongReceived = Date.now();
  socket.on("pong_heartbeat", () => {
    metadata.lastPongReceived = Date.now();
  });

  metadata.pingInterval = setInterval(() => {
    if (socket.connected) socket.emit("ping_heartbeat", { timestamp: Date.now() });
  }, 15000);

  metadata.healthCheckInterval = setInterval(() => {
    const timeSinceLastPong = Date.now() - metadata.lastPongReceived;
    if (timeSinceLastPong > 45000 && socket.connected) {
      socket.disconnect().connect();
    }
  }, 20000);
};

const registerEventListeners = (metadata: SocketMetadata, key: Realtime_item, dispatch: rootDispatch) => {
  if (metadata.listenersRegistered) return;
  const socket = metadata.socket;

  ["create", "update", "delete"].forEach((evt) => {
    socket.on(evt, (data) => {
      const actions = collectionActions[key];
      if (actions?.[evt]) dispatch(actions[evt](data));
    });
  });

  if (key === "flux") {
    socket.on("flux-mounted", (mounted: string[]) => {
      dispatch(flux.setMounted(mounted))
    })
  }

  if (key === "live") {
    socket.on("init_status", (data) => dispatch(live.reset(data)));
    socket.on("icecast_flux", (data) => dispatch(live.set_flux(data)));
    socket.on("init_audio", (data) => dispatch(live.set_audio(data)));
    socket.on("init_video", (data) => dispatch(live.set_video(data)));
    socket.on("metadata", (data) => dispatch(live.reset_metadata(data)));
  }

  socket.on("refresh_operator", (op) => {
    if (op.status === "enter") dispatch(operator_connected.merge(op.ids));
    else if (op.status === "exit") dispatch(operator_connected.pop(op.ids));
  });

  socket.on("refresh_client", (cl) => {
    if (cl.status === "enter") dispatch(client_connected.merge(cl.ids));
    else if (cl.status === "exit") dispatch(client_connected.pop(cl.ids));
  });

  socket.on("refresh_admin", (ad) => {
    if (ad.status === "enter") dispatch(admin_connected.merge(ad.ids));
    else if (ad.status === "exit") dispatch(admin_connected.pop(ad.ids));
  });

  socket.on("refresh_guest", (guest) => dispatch(guest_connected.set(guest)));

  socket.on("has_signed", () => {
    dispatch(setStatus({ key, status: "signed" }));
  });

  metadata.listenersRegistered = true;
};

const createSocket = (key: Realtime_item, gateway: string, dispatch: rootDispatch): SocketMetadata => {
  const socket = io(`${gateway}/${key}`, {
    transports: ["websocket"],
    reconnection: true,
    reconnectionDelay: 2000,
    reconnectionDelayMax: 5000,
    autoConnect: true,
  });

  const metadata: SocketMetadata = {
    socket,
    lastPongReceived: Date.now(),
    listenersRegistered: false,
  };

  socket.on("connect", () => {
    dispatch(setStatus({ key, status: "connected" }));
    registerEventListeners(metadata, key, dispatch);
    setupPingPong(metadata, key);
  });

  socket.on("disconnect", () => {
    dispatch(setStatus({ key, status: "disconnected" }));
    if (metadata.pingInterval) clearInterval(metadata.pingInterval);
    if (metadata.healthCheckInterval) clearInterval(metadata.healthCheckInterval);
    metadata.listenersRegistered = false;
  });

  socket.on("connect_error", (err) => {
    console.warn(`[Realtime] ${key} connection error:`, err.message);
    dispatch(setStatus({ key, status: "disconnected" }));
  });

  return metadata;
};

export const RealtimeMiddleware: Middleware = (store: MiddlewareAPI<rootDispatch, rootState>) => {
  const dispatch = store.dispatch;
  const gateway = process.env.NEXT_PUBLIC_GATEWAY || "http://localhost:4000";

  if (typeof window !== "undefined") {
    const handleReconnection = () => {
      clients.forEach((metadata) => {
        if (!metadata.socket.connected && !metadata.socket.active) {
          metadata.socket.connect();
        }
      });
    };

    window.addEventListener("online", handleReconnection);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") handleReconnection();
    });
  }

  return (next) => (action: any) => {
    if (typeof window === "undefined") return next(action);

    const key = action.payload?.key as Realtime_item;

    if (action.type === "realtime_slice/connect") {
      if (clients.has(key)) {
        const meta = clients.get(key)!;
        if (meta.socket.connected || meta.socket.active) return next(action);
        cleanupSocket(key);
      }
      clients.set(key, createSocket(key, gateway, dispatch));
    } 
    
    else if (action.type === "realtime_slice/disconnect") {
      cleanupSocket(key);
      dispatch(setStatus({ key, status: "disconnected" }));
    } 
    
    else if (action.type === "realtime_slice/emit") {
      const meta = clients.get(key);
      if (meta?.socket.connected) {
        meta.socket.emit(action.payload.event, action.payload.data);
      }
    } 
    
    else if (action.type === "realtime_slice/signin") {
      const meta = clients.get(key);
      if (meta?.socket.connected) {
        meta.socket.emit("signin", action.payload.session);
      }
    } 
    
    else if (action.type === "realtime_slice/signout_all") {
      clients.forEach((meta) => {
        if (meta.socket.connected) meta.socket.emit("signout", action.payload);
      });
    }

    return next(action);
  };
};